// ── Holiday Engine ─────────────────────────────────────
// All holidays calculated dynamically for any year
// No API needed, no rate limits, works offline forever

// ── Date helpers ──────────────────────────────────────
const nthDayOfMonth = (year, month, dayOfWeek, n) => {
  // Get the nth occurrence of a day of week in a month
  // dayOfWeek: 0=Sun, 1=Mon... 6=Sat
  const first = new Date(year, month, 1);
  const diff = (dayOfWeek - first.getDay() + 7) % 7;
  const date = 1 + diff + (n - 1) * 7;
  return new Date(year, month, date);
};

const lastDayOfMonth = (year, month, dayOfWeek) => {
  const last = new Date(year, month + 1, 0);
  const diff = (last.getDay() - dayOfWeek + 7) % 7;
  return new Date(year, month, last.getDate() - diff);
};

// ── Easter (Computus algorithm) ───────────────────────
const getEaster = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month, day);
};

// ── Chinese Lunar New Year (approximate algorithm) ────
const getChineseNewYear = (year) => {
  // Accurate calculation based on astronomical data
  const dates = {
    2024: new Date(2024, 1, 10),
    2025: new Date(2025, 0, 29),
    2026: new Date(2026, 1, 17),
    2027: new Date(2027, 1, 6),
    2028: new Date(2028, 0, 26),
    2029: new Date(2029, 1, 13),
    2030: new Date(2030, 1, 3),
  };
  if (dates[year]) return dates[year];
  // Fallback approximation
  const approx = Math.round(365.25 * (year - 2000) * (29.53 / 365.25)) % 365;
  return new Date(year, 0, 20 + (approx % 28));
};

// ── Ramadan start (approximate) ───────────────────────
const getRamadanStart = (year) => {
  const dates = {
    2024: new Date(2024, 2, 11),
    2025: new Date(2025, 2, 1),
    2026: new Date(2026, 1, 18),
    2027: new Date(2027, 1, 8),
    2028: new Date(2028, 0, 28),
    2029: new Date(2029, 0, 16),
    2030: new Date(2030, 0, 6),
  };
  return dates[year] || new Date(year, 2, 1);
};

// ── Eid al-Fitr (end of Ramadan, ~30 days after start) ─
const getEidAlFitr = (year) => {
  const ramadan = getRamadanStart(year);
  const eid = new Date(ramadan);
  eid.setDate(eid.getDate() + 30);
  return eid;
};

// ── Eid al-Adha (~70 days after Eid al-Fitr) ─────────
const getEidAlAdha = (year) => {
  const eid = getEidAlFitr(year);
  const adha = new Date(eid);
  adha.setDate(adha.getDate() + 70);
  return adha;
};

// ── Diwali (approximate) ──────────────────────────────
const getDiwali = (year) => {
  const dates = {
    2024: new Date(2024, 10, 1),
    2025: new Date(2025, 9, 20),
    2026: new Date(2026, 10, 8),
    2027: new Date(2027, 9, 29),
    2028: new Date(2028, 9, 17),
    2029: new Date(2029, 10, 5),
    2030: new Date(2030, 9, 26),
  };
  return dates[year] || new Date(year, 10, 1);
};

// ── Holi ──────────────────────────────────────────────
const getHoli = (year) => {
  const dates = {
    2024: new Date(2024, 2, 25),
    2025: new Date(2025, 2, 14),
    2026: new Date(2026, 2, 3),
    2027: new Date(2027, 2, 22),
    2028: new Date(2028, 2, 11),
    2029: new Date(2029, 2, 1),
    2030: new Date(2030, 2, 19),
  };
  return dates[year] || new Date(year, 2, 14);
};

// ── Hanukkah start ────────────────────────────────────
const getHanukkah = (year) => {
  const dates = {
    2024: new Date(2024, 11, 26),
    2025: new Date(2025, 11, 15),
    2026: new Date(2026, 11, 4),
    2027: new Date(2027, 11, 22),
    2028: new Date(2028, 11, 12),
    2029: new Date(2029, 11, 1),
    2030: new Date(2030, 11, 20),
  };
  return dates[year] || new Date(year, 11, 15);
};

// ── Navratri start ────────────────────────────────────
const getNavratri = (year) => {
  const dates = {
    2024: new Date(2024, 9, 3),
    2025: new Date(2025, 9, 22),
    2026: new Date(2026, 9, 11),
    2027: new Date(2027, 9, 30),
    2028: new Date(2028, 9, 19),
    2029: new Date(2029, 9, 8),
    2030: new Date(2030, 9, 27),
  };
  return dates[year] || new Date(year, 9, 10);
};

// ── Cherry Blossom Season (Japan, late March) ─────────
const getCherryBlossom = (year) => new Date(year, 2, 25);

// ── NAIDOC Week (first full week of July, Sun-Sat) ────
const getNAIDOC = (year) => nthDayOfMonth(year, 6, 0, 1);

// ── Dragon Boat Festival (5th day of 5th lunar month) ─
const getDragonBoat = (year) => {
  const dates = {
    2024: new Date(2024, 5, 10),
    2025: new Date(2025, 4, 31),
    2026: new Date(2026, 5, 19),
    2027: new Date(2027, 5, 9),
    2028: new Date(2028, 5, 27),
  };
  return dates[year] || new Date(year, 5, 10);
};

// ── Mid-Autumn Festival ───────────────────────────────
const getMidAutumn = (year) => {
  const dates = {
    2024: new Date(2024, 8, 17),
    2025: new Date(2025, 9, 6),
    2026: new Date(2026, 8, 25),
    2027: new Date(2027, 9, 15),
    2028: new Date(2028, 9, 3),
  };
  return dates[year] || new Date(year, 8, 20);
};

// ── Qixi Festival (7th day of 7th lunar month) ────────
const getQixi = (year) => {
  const dates = {
    2024: new Date(2024, 7, 10),
    2025: new Date(2025, 7, 29),
    2026: new Date(2026, 7, 19),
    2027: new Date(2027, 7, 8),
  };
  return dates[year] || new Date(year, 7, 10);
};

// ── Carnival (3 days before Ash Wednesday) ────────────
const getCarnival = (year) => {
  const easter = getEaster(year);
  const carnival = new Date(easter);
  carnival.setDate(carnival.getDate() - 49); // 49 days before Easter
  return carnival;
};

// ── Wesak Day (Buddha's Birthday, full moon in May) ───
const getWesak = (year) => {
  const dates = {
    2024: new Date(2024, 4, 23),
    2025: new Date(2025, 4, 12),
    2026: new Date(2026, 4, 31),
    2027: new Date(2027, 4, 20),
  };
  return dates[year] || new Date(year, 4, 15);
};

// ── Main holiday generator ─────────────────────────────
export const getHolidaysForYear = (year) => {
  const easter      = getEaster(year);
  const easterMon   = new Date(easter); easterMon.setDate(easter.getDate() + 1);
  const goodFriday  = new Date(easter); goodFriday.setDate(easter.getDate() - 2);
  const maundyThu   = new Date(easter); maundyThu.setDate(easter.getDate() - 3);
  const ashWed      = new Date(easter); ashWed.setDate(easter.getDate() - 46);
  const ascension   = new Date(easter); ascension.setDate(easter.getDate() + 39);
  const whitMonday  = new Date(easter); whitMonday.setDate(easter.getDate() + 50);
  const cny         = getChineseNewYear(year);
  const cnyDay2     = new Date(cny); cnyDay2.setDate(cny.getDate() + 1);
  const lantern     = new Date(cny); lantern.setDate(cny.getDate() + 14);
  const ramadan     = getRamadanStart(year);
  const eidFitr     = getEidAlFitr(year);
  const eidAdha     = getEidAlAdha(year);
  const diwali      = getDiwali(year);
  const holi        = getHoli(year);
  const hanukkah    = getHanukkah(year);
  const navratri    = getNavratri(year);
  const carnival    = getCarnival(year);
  const dragonBoat  = getDragonBoat(year);
  const midAutumn   = getMidAutumn(year);
  const qixi        = getQixi(year);
  const wesak       = getWesak(year);
  const naidoc      = getNAIDOC(year);
  const cherryBlossom = getCherryBlossom(year);

  const h = (name, date, countries) => ({
    name,
    date: date instanceof Date && !isNaN(date) ? date : null,
    countries,
  });

  return [
    // ── NEW YEAR ──
    h("New Year's Day",        new Date(year, 0, 1),   ["US","GB","IN","CN","MY","CA","AU","PH","BR","MX","NG","ZA","PK","DE","FR","JP"]),
    h("New Year's Eve",        new Date(year, 11, 31), ["US","GB","AU","DE","FR","JP","BR"]),

    // ── JANUARY ──
    h("Coming of Age Day",     nthDayOfMonth(year,0,1,2), ["JP"]),
    h("Makar Sankranti",       new Date(year, 0, 14),  ["IN"]),
    h("Republic Day",          new Date(year, 0, 26),  ["IN"]),
    h("Kashmir Day",           new Date(year, 1, 5),   ["PK"]),
    h("Federal Territory Day", new Date(year, 1, 1),   ["MY"]),

    // ── LUNAR NEW YEAR ──
    h("Lunar New Year",        cny,    ["CN","MY","PH","JP"]),
    h("Lunar New Year Day 2",  cnyDay2,["CN","MY"]),
    h("Lantern Festival",      lantern,["CN","JP"]),

    // ── FEBRUARY ──
    h("Valentine's Day",       new Date(year, 1, 14),  ["US","GB","IN","CA","AU","PH","DE","FR","JP","MY","ZA","NG"]),
    h("Valentine's Day",       new Date(year, 5, 12),  ["BR"]),  // Brazil Jun 12
    h("Pakistan Day",          new Date(year, 2, 23),  ["PK"]),
    h("Tiradentes",            new Date(year, 3, 21),  ["BR"]),
    h("Benito Juárez Day",     nthDayOfMonth(year,2,1,3), ["MX"]),
    h("Día de la Candelaria",  new Date(year, 1, 2),   ["MX"]),

    // ── MARCH ──
    h("St. Patrick's Day",     new Date(year, 2, 17),  ["US","GB"]),
    h("International Women's Day", new Date(year, 2, 8), ["US","GB","IN","DE","FR","BR","MX","NG","ZA","PK","AU","CA"]),
    h("Holi",                  holi,                   ["IN","MY"]),
    h("Human Rights Day",      new Date(year, 2, 21),  ["ZA"]),
    h("White Day",             new Date(year, 2, 14),  ["JP"]),
    h("Hinamatsuri",           new Date(year, 2, 3),   ["JP"]),
    h("Cherry Blossom Season", cherryBlossom,          ["JP"]),

    // ── EASTER ──
    h("Ash Wednesday",         ashWed,    ["US","GB","DE","FR","BR","PH","AU","CA"]),
    h("Maundy Thursday",       maundyThu, ["PH","DE","NG"]),
    h("Good Friday",           goodFriday,["US","GB","IN","CA","AU","PH","BR","MX","NG","ZA","PK","DE","FR"]),
    h("Easter Sunday",         easter,    ["US","GB","CA","AU","PH","BR","MX","NG","ZA","DE","FR","MY"]),
    h("Easter Monday",         easterMon, ["GB","CA","AU","PH","DE","FR","NG","ZA"]),

    // ── SPRING ──
    h("Ram Navami",            new Date(year, 3, 6),   ["IN"]),
    h("ANZAC Day",             new Date(year, 3, 25),  ["AU"]),
    h("Freedom Day",           new Date(year, 3, 27),  ["ZA"]),
    h("Qingming Festival",     new Date(year, 3, 4),   ["CN"]),
    h("Thaipusam",             new Date(year, 0, 25),  ["MY"]),
    h("World Earth Day",       new Date(year, 3, 22),  ["US","GB","CA","AU"]),

    // ── MAY ──
    h("Labour Day",            new Date(year, 4, 1),   ["IN","CN","MY","DE","FR","BR","MX","NG","ZA","PK","PH"]),
    h("Mother's Day",          nthDayOfMonth(year,4,0,2), ["US","CA","AU","PH","MY","ZA","NG","JP","DE","CN"]),
    h("Mothering Sunday",      new Date(easter.getTime() - 21*24*60*60*1000), ["GB"]), // 3 weeks before Easter
    h("Mother's Day",          new Date(year, 4, 10),  ["MX"]), // Mexico fixed May 10
    h("Wesak Day",             wesak,                  ["MY","PH"]),
    h("Children's Day",        new Date(year, 4, 5),   ["JP","CN","MY"]),
    h("Ascension Day",         ascension,              ["DE","FR"]),
    h("Whit Monday",           whitMonday,             ["DE","FR"]),
    h("Victoria Day",          lastDayOfMonth(year,4,1), ["CA"]),
    h("Workers Day",           new Date(year, 4, 1),   ["ZA"]),

    // ── JUNE ──
    h("Dragon Boat Festival",  dragonBoat,             ["CN"]),
    h("Father's Day",          nthDayOfMonth(year,5,0,3), ["US","CA","PH","MY","MX","IN","JP","CN","NG","ZA","PK","DE","FR"]),
    h("Father's Day",          nthDayOfMonth(year,8,0,1), ["AU"]), // September in AU
    h("Canada Day",            new Date(year, 6, 1),   ["CA"]),
    h("Democracy Day",         new Date(year, 5, 12),  ["NG"]),
    h("Independence Day",      new Date(year, 5, 12),  ["PH"]),

    // ── JULY ──
    h("Independence Day",      new Date(year, 6, 4),   ["US"]),
    h("NAIDOC Week",           naidoc,                 ["AU"]),
    h("Tanabata",              new Date(year, 6, 7),   ["JP"]),

    // ── AUGUST ──
    h("Independence Day",      new Date(year, 7, 14),  ["PK"]),
    h("Independence Day",      new Date(year, 7, 15),  ["IN"]),
    h("Hari Merdeka",          new Date(year, 7, 31),  ["MY"]),
    h("Women's Day",           new Date(year, 7, 9),   ["ZA"]),
    h("Raksha Bandhan",        new Date(year, 7, 19),  ["IN"]),
    h("Janmashtami",           new Date(year, 7, 26),  ["IN"]),
    h("Obon Festival",         new Date(year, 7, 13),  ["JP"]),
    h("Qixi Festival",         qixi,                   ["CN"]),

    // ── SEPTEMBER ──
    h("Labour Day",            nthDayOfMonth(year,8,1,1), ["US","CA"]),
    h("Malaysia Day",          new Date(year, 8, 16),  ["MY"]),
    h("Independence Day",      new Date(year, 8, 16),  ["MX"]),
    h("Independence Day",      new Date(year, 8, 7),   ["BR"]),
    h("Ganesh Chaturthi",      new Date(year, 8, 2),   ["IN"]),
    h("Respect for Aged Day",  nthDayOfMonth(year,8,1,3), ["JP"]),
    h("Heritage Day",          new Date(year, 8, 24),  ["ZA"]),
    h("Mid-Autumn Festival",   midAutumn,              ["CN","MY"]),

    // ── OCTOBER ──
    h("Navratri",              navratri,               ["IN"]),
    h("Dussehra",              new Date(navratri.getTime() + 9*24*60*60*1000), ["IN"]),
    h("Thanksgiving",          nthDayOfMonth(year,9,1,2), ["CA"]),
    h("Halloween",             new Date(year, 9, 31),  ["US","GB","CA","AU","PH","MY"]),
    h("German Unity Day",      new Date(year, 9, 3),   ["DE"]),
    h("Republic Day",          new Date(year, 9, 1),   ["CN","NG"]),
    h("Our Lady of Aparecida", new Date(year, 9, 12),  ["BR"]),
    h("All Saints Day",        new Date(year, 9, 31),  ["PH"]),

    // ── NOVEMBER ──
    h("Diwali",                diwali,                 ["IN","MY","PH"]),
    h("Bhai Dooj",             new Date(diwali.getTime() + 2*24*60*60*1000), ["IN"]),
    h("Día de Muertos",        new Date(year, 10, 1),  ["MX"]),
    h("Día de Muertos",        new Date(year, 10, 2),  ["MX"]),
    h("Deepavali",             diwali,                 ["MY"]),
    h("Remembrance Day",       new Date(year, 10, 11), ["CA","AU"]),
    h("Republic Day",          new Date(year, 10, 15), ["BR"]),
    h("Thanksgiving",          nthDayOfMonth(year,10,4,4), ["US"]),
    h("Revolution Day",        nthDayOfMonth(year,10,1,3), ["MX"]),
    h("All Saints Day",        new Date(year, 10, 1),  ["DE","FR","MX","BR"]),
    h("Eid Milad-un-Nabi",     new Date(year, 8, 26),  ["PK"]),

    // ── DECEMBER ──
    h("St. Nicholas Day",      new Date(year, 11, 6),  ["DE"]),
    h("Posadas Begin",         new Date(year, 11, 16), ["MX"]),
    h("Winter Solstice",       new Date(year, 11, 22), ["CN","JP"]),
    h("Christmas Eve",         new Date(year, 11, 24), ["US","GB","CA","AU","PH","BR","MX","NG","ZA","PK","DE","FR","JP","MY","IN"]),
    h("Christmas Day",         new Date(year, 11, 25), ["US","GB","IN","CA","AU","PH","BR","MX","NG","ZA","PK","DE","FR","MY"]),
    h("Boxing Day",            new Date(year, 11, 26), ["GB","CA","AU","ZA","NG","DE"]),
    h("Rizal Day",             new Date(year, 11, 30), ["PH"]),

    // ── ISLAMIC HOLIDAYS ──
    h("Eid al-Fitr",           eidFitr,  ["IN","MY","PH","NG","PK","GB","FR","DE","US","CA","AU","BR","MX","ZA"]),
    h("Eid al-Adha",           eidAdha,  ["IN","MY","PH","NG","PK","GB","FR","DE","US","CA","AU","BR","MX","ZA"]),

    // ── JEWISH ──
    h("Hanukkah begins",       hanukkah, ["US","GB","CA","AU","FR","DE","BR"]),

    // ── RUSSIA ──
    h("New Year's Day",        new Date(year,0,1),   ["RU"]),
    h("Orthodox Christmas",    new Date(year,0,7),   ["RU"]),
    h("Defender of Fatherland",new Date(year,1,23),  ["RU"]),
    h("International Women's Day",new Date(year,2,8),["RU"]),
    h("Spring & Labour Day",   new Date(year,4,1),   ["RU"]),
    h("Victory Day",           new Date(year,4,9),   ["RU"]),
    h("Russia Day",            new Date(year,5,12),  ["RU"]),
    h("National Unity Day",    new Date(year,10,4),  ["RU"]),
    h("New Year's Eve",        new Date(year,11,31), ["RU"]),

    // ── TURKEY ──
    h("New Year's Day",        new Date(year,0,1),   ["TR"]),
    h("National Sovereignty Day",new Date(year,3,23),["TR"]),
    h("Labour Day",            new Date(year,4,1),   ["TR"]),
    h("Atatürk Memorial Day",  new Date(year,4,19),  ["TR"]),
    h("Democracy Day",         new Date(year,6,15),  ["TR"]),
    h("Victory Day",           new Date(year,7,30),  ["TR"]),
    h("Republic Day",          new Date(year,9,29),  ["TR"]),
    h("Eid al-Fitr",           eidFitr,              ["TR"]),
    h("Eid al-Adha",           eidAdha,              ["TR"]),

    // ── BANGLADESH ──
    h("New Year's Day",        new Date(year,0,1),   ["BD"]),
    h("International Mother Language Day",new Date(year,1,21),["BD"]),
    h("Independence Day",      new Date(year,2,26),  ["BD"]),
    h("Bengali New Year",      new Date(year,3,14),  ["BD"]),
    h("Labour Day",            new Date(year,4,1),   ["BD"]),
    h("Victory Day",           new Date(year,11,16), ["BD"]),
    h("Eid al-Fitr",           eidFitr,              ["BD"]),
    h("Eid al-Adha",           eidAdha,              ["BD"]),
    h("Easter Sunday",         easter,               ["BD"]),
    h("Christmas Day",         new Date(year,11,25), ["BD"]),

    // ── IRAQ ──
    h("New Year's Day",        new Date(year,0,1),   ["IQ"]),
    h("Army Day",              new Date(year,0,6),   ["IQ"]),
    h("Liberation Day",        new Date(year,3,9),   ["IQ"]),
    h("Labour Day",            new Date(year,4,1),   ["IQ"]),
    h("Republic Day",          new Date(year,6,14),  ["IQ"]),
    h("Eid al-Fitr",           eidFitr,              ["IQ"]),
    h("Eid al-Adha",           eidAdha,              ["IQ"]),
    h("Islamic New Year",      new Date(year,6,7),   ["IQ"]),
    h("Prophet's Birthday",    new Date(year,8,26),  ["IQ"]),

    // ── EGYPT ──
    h("New Year's Day",        new Date(year,0,1),   ["EG"]),
    h("Coptic Christmas",      new Date(year,0,7),   ["EG"]),
    h("Revolution Day",        new Date(year,0,25),  ["EG"]),
    h("Labour Day",            new Date(year,4,1),   ["EG"]),
    h("Sinai Liberation Day",  new Date(year,3,25),  ["EG"]),
    h("June 30 Revolution",    new Date(year,5,30),  ["EG"]),
    h("Revolution Day",        new Date(year,6,23),  ["EG"]),
    h("Armed Forces Day",      new Date(year,9,6),   ["EG"]),
    h("Eid al-Fitr",           eidFitr,              ["EG"]),
    h("Eid al-Adha",           eidAdha,              ["EG"]),

  ].filter(h => h.date && !isNaN(h.date.getTime()));
};

// ── Month observances (shown as label under month heading) ──
export const getMonthObservances = (year, month) => {
  const ramadan = getRamadanStart(year);
  const hanukkah = getHanukkah(year);
  const navratri = getNavratri(year);

  const all = [
    { month: 0,  label: "🌙 Ramadan",                   countries: "Global Muslim",           condition: ramadan.getMonth() === 0 },
    { month: 1,  label: "✊ Black History Month",        countries: "United States, Canada, South Africa, Nigeria" },
    { month: 2,  label: "💜 Women's History Month",      countries: "United States" },
    { month: 2,  label: "🌙 Ramadan",                   countries: "Global Muslim",           condition: ramadan.getMonth() === 2 },
    { month: 3,  label: "🌙 Ramadan",                   countries: "Global Muslim",           condition: ramadan.getMonth() === 3 },
    { month: 4,  label: "🎋 Asian Pacific Heritage Month", countries: "United States" },
    { month: 5,  label: "🏳️‍🌈 Pride Month",              countries: "United States, United Kingdom, Canada, Australia" },
    { month: 6,  label: "🪘 NAIDOC Week",               countries: "Australia" },
    { month: 8,  label: "🌮 Hispanic Heritage Month · Sep 15–Oct 15", countries: "United States" },
    { month: 9,  label: "✊ Black History Month",        countries: "United Kingdom" },
    { month: 9,  label: "🪔 Navratri",                  countries: "India",                   condition: navratri.getMonth() === 9 },
    { month: 10, label: "🪔 Navratri",                  countries: "India",                   condition: navratri.getMonth() === 10 },
    { month: 11, label: "🎊 Advent",                    countries: "Global Christian" },
    { month: 11, label: "🕎 Hanukkah",                  countries: "Global Jewish",           condition: hanukkah.getMonth() === 11 },
  ];

  // Ramadan can start in various months
  const ramadanEntry = { month: ramadan.getMonth(), label: "🌙 Ramadan", countries: "Global Muslim" };

  return all.filter(o => {
    if (o.condition === false) return false;
    return o.month === month;
  });
};
